import React, { useEffect } from "react";
import {
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Field, Form, useFormikContext } from "formik";
import * as Yup from "yup";

// 👇 Custom component to auto-submit when form is valid
function AutoSubmitForm() {
  const { isValid, dirty, submitForm } = useFormikContext();

  useEffect(() => {
    if (isValid && dirty) {
      submitForm(); // Only triggers submit, do NOT call onFormSubmit here
    }
  }, [isValid, dirty, submitForm]);

  return null;
}

function TravelerDetails({ type, count, countries, passportRequired, onFormSubmit }) {
  const initialValues = {
    travelers: Array.from({ length: count }).map(() => ({
      title: "",
      firstName: "",
      lastName: "",
      dob: "",
      gender: "",
      nationality: "",
      passportNo: "",
      passportExpiry: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      countryCode: "",
      country: "",
    })),
  };

  const validationSchema = Yup.object({
    travelers: Yup.array().of(
      Yup.object({
        title: Yup.string().required("Title is required"),
        firstName: Yup.string().required("First Name is required"),
        lastName: Yup.string().required("Last Name is required"),
        dob: Yup.date().required("Date of Birth is required"),
        gender: Yup.string().required("Gender is required"),
        nationality: passportRequired
          ? Yup.string().required("Nationality is required")
          : Yup.string(),
        passportNo: passportRequired
          ? Yup.string().required("Passport No. is required")
          : Yup.string(),
        passportExpiry: passportRequired
          ? Yup.date().required("Passport Expiry is required")
          : Yup.date(),
        addressLine1: Yup.string().required("Address Line 1 is required"),
        addressLine2: Yup.string().required("Address Line 2 is required"),
        city: Yup.string().required("City is required"),
        countryCode: Yup.string().required("Country Code is required"),
        country: Yup.string().required("Country is required"),
      })
    ),
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnChange={true}
      validateOnBlur={true}
      onSubmit={(values) => {
        alert("All traveler details filled correctly. Submitting...");
        onFormSubmit(values);
      }}
    >

      {({ values }) => (
        <Form>
          <AutoSubmitForm onFormSubmit={() => { }} />

          {values.travelers.map((_, index) => (
            <div key={`traveler-${index}`} style={{ marginTop: "16px" }}>
              <Typography fontWeight={600} fontSize="16px">
                {type.charAt(0).toUpperCase() + type.slice(1)} {index + 1}
              </Typography>

              <Grid container spacing={2} marginTop={1}>
                <Grid item md={2}>
                  <Field
                    as={TextField}
                    fullWidth
                    select
                    label="Title*"
                    name={`travelers[${index}].title`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  >
                    <MenuItem value="Mr">Mr.</MenuItem>
                    <MenuItem value="Ms">Ms.</MenuItem>
                    <MenuItem value="Mrs">Mrs.</MenuItem>
                  </Field>
                </Grid>

                <Grid item md={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="First Name*"
                    name={`travelers[${index}].firstName`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Last Name*"
                    name={`travelers[${index}].lastName`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    type="date"
                    label="DOB*"
                    name={`travelers[${index}].dob`}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={2}>
                  <Field
                    as={TextField}
                    fullWidth
                    select
                    label="Gender*"
                    name={`travelers[${index}].gender`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  >
                    <MenuItem value="1">Male</MenuItem>
                    <MenuItem value="2">Female</MenuItem>
                    <MenuItem value="3">Other</MenuItem>
                  </Field>
                </Grid>

                {passportRequired && (
                  <>
                    <Grid item md={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        select
                        label="Nationality"
                        name={`travelers[${index}].nationality`}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      >
                        {countries.map((country) => (
                          <MenuItem key={country.code} value={country.nationality}>
                            {country.nationality}
                          </MenuItem>
                        ))}
                      </Field>
                    </Grid>

                    <Grid item md={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        label="Passport No."
                        name={`travelers[${index}].passportNo`}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>

                    <Grid item md={3}>
                      <Field
                        as={TextField}
                        fullWidth
                        type="date"
                        label="Passport Expiry"
                        name={`travelers[${index}].passportExpiry`}
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ sx: { borderRadius: 3 } }}
                      />
                    </Grid>
                  </>
                )}

                <Grid item md={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address Line 1*"
                    name={`travelers[${index}].addressLine1`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={4}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="Address Line 2*"
                    name={`travelers[${index}].addressLine2`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={3}>
                  <Field
                    as={TextField}
                    fullWidth
                    label="City*"
                    name={`travelers[${index}].city`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  />
                </Grid>

                <Grid item md={2}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    label="Country Code"
                    name={`travelers[${index}].countryCode`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  >
                    <MenuItem value="">Select Country Code</MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {country.code}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>

                <Grid item md={3}>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    label="Country"
                    name={`travelers[${index}].country`}
                    InputProps={{ sx: { borderRadius: 3 } }}
                  >
                    <MenuItem value="">Select Country</MenuItem>
                    {countries.map((country) => (
                      <MenuItem key={country.name} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Field>
                </Grid>
              </Grid>
            </div>
          ))}
        </Form>
      )}
    </Formik>
  );
}

export default TravelerDetails;
